import React, { useState } from 'react';
import { ScoreDispute } from '../types';
import { updateDisputeStatus } from '../api';
import './DisputeResponseModal.css';

interface DisputeResponseModalProps {
  isOpen: boolean;
  onClose: () => void;
  dispute: ScoreDispute | null;
  onSuccess?: () => void;
}

const DisputeResponseModal: React.FC<DisputeResponseModalProps> = ({
  isOpen,
  onClose,
  dispute,
  onSuccess,
}) => {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<'success' | 'failed' | 'bad_task' | null>(null);
  const [disputeDecision, setDisputeDecision] = useState<'accepted' | 'rejected' | null>(null);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [stillMarkAsBadTask, setStillMarkAsBadTask] = useState(false);

  if (!isOpen || !dispute) return null;

  // Check if this is a bad task dispute from the label platform
  const isBadTaskFromLabelPlatform = dispute.note?.startsWith('Marked as Bad Task from the Label Platform for reason:');

  const getDefaultStatus = (decision: 'accepted' | 'rejected'): 'success' | 'failed' | 'bad_task' | null => {
    if (decision === 'accepted') {
      return dispute.proposed_score;
    } else {
      // For rejected bad task disputes from label platform, return null (no final status)
      if (isBadTaskFromLabelPlatform) {
        return null;
      }
      // For rejected disputes, default to current status
      if (dispute.final_score === 1) return 'success';
      if (dispute.final_score === 0) return 'failed';
      return 'failed'; // Default if no score
    }
  };

  const getExpectedStatus = (decision: 'accepted' | 'rejected'): string | null => {
    const status = getDefaultStatus(decision);
    if (status === null) return null;
    if (status === 'success') return 'Success';
    if (status === 'failed') return 'Failed';
    return 'Bad Task';
  };

  const getStatusDisplayName = (status: 'success' | 'failed' | 'bad_task'): string => {
    if (status === 'success') return 'Success';
    if (status === 'failed') return 'Failed';
    return 'Bad Task';
  };

  const handleSubmit = () => {
    if (!disputeDecision) {
      alert('Please accept or reject the dispute.');
      return;
    }

    if (!reason.trim()) {
      alert('Please provide a reason for your decision.');
      return;
    }

    const expectedStatus = getDefaultStatus(disputeDecision);

    // For rejected bad task disputes, we don't require a final status
    if (expectedStatus !== null && !selectedStatus) {
      alert('Please select a final status for the problem.');
      return;
    }

    if (expectedStatus !== null && selectedStatus !== expectedStatus) {
      setShowWarningModal(true);
    } else {
      submitDispute(disputeDecision);
    }
  };

  const submitDispute = async (decision: 'accepted' | 'rejected') => {
    setIsSubmitting(true);
    try {
      // Determine final status to send
      let finalStatusToSend: 'success' | 'failed' | 'bad_task' | undefined;

      if (isBadTaskFromLabelPlatform && decision === 'rejected' && stillMarkAsBadTask) {
        // Reject the dispute but still mark the problem as bad task
        finalStatusToSend = 'bad_task';
      } else {
        finalStatusToSend = selectedStatus || undefined;
      }

      await updateDisputeStatus(dispute.id, decision, reason, finalStatusToSend);
      // Reset form
      setReason('');
      setSelectedStatus(null);
      setDisputeDecision(null);
      setStillMarkAsBadTask(false);
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error updating dispute status:', error);
      alert('Failed to update dispute status. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Set default status when dispute decision changes
  const handleDisputeDecisionClick = (decision: 'accepted' | 'rejected') => {
    setDisputeDecision(decision);
    setStillMarkAsBadTask(false); // Reset this when changing decision
    // For bad task disputes from label platform, automatically set to bad_task when accepting, null when rejecting
    if (isBadTaskFromLabelPlatform && decision === 'accepted') {
      setSelectedStatus('bad_task');
    } else if (isBadTaskFromLabelPlatform && decision === 'rejected') {
      setSelectedStatus(null);
    } else {
      setSelectedStatus(getDefaultStatus(decision));
    }
  };

  return (
    <div className="dispute-response-modal-overlay" onClick={onClose}>
      <div className="dispute-response-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dispute-response-modal-header">
          <h2>Respond to Dispute</h2>
          <button className="dispute-response-modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="dispute-response-modal-content">
          <div className="dispute-info">
            <p className="problem-name"><strong>Problem:</strong> {dispute.problem_name}</p>
            <div className="score-comparison">
              <div className="score-item">
                <span className="score-label">Current Score:</span>
                <span className={`score-value ${dispute.final_score === 1 ? 'success' : dispute.final_score === 0 ? 'failed' : ''}`}>
                  {dispute.final_score === 1 ? '✓ Success' : dispute.final_score === 0 ? '✗ Failed' : 'Not scored'}
                </span>
              </div>
              <div className="score-item">
                <span className="score-label">Disputed Score:</span>
                <span className={`score-value ${dispute.proposed_score}`}>
                  {dispute.proposed_score === 'success' ? '✓ Success' : 
                   dispute.proposed_score === 'failed' ? '✗ Failed' : 
                   '⚠ Bad Task'}
                </span>
              </div>
            </div>
            
            <div className="dispute-note-section">
              <p className="note-label"><strong>Dispute Note:</strong></p>
              <p className="note-content">{dispute.note}</p>
            </div>
          </div>
          
          <div className="response-form">
            <div className="dispute-decision-section">
              <label>Accept or Reject Dispute:</label>
              <div className="decision-buttons">
                <button
                  type="button"
                  className={`decision-btn accept-decision ${disputeDecision === 'accepted' ? 'selected' : ''}`}
                  onClick={() => handleDisputeDecisionClick('accepted')}
                >
                  ✓ Accept Dispute
                </button>
                <button
                  type="button"
                  className={`decision-btn reject-decision ${disputeDecision === 'rejected' ? 'selected' : ''}`}
                  onClick={() => handleDisputeDecisionClick('rejected')}
                >
                  ✗ Reject Dispute
                </button>
              </div>
            </div>

            {/* For bad task disputes: show checkbox to still mark as bad when rejecting */}
            {isBadTaskFromLabelPlatform && disputeDecision === 'rejected' && (
              <div className="still-mark-bad-task-section">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={stillMarkAsBadTask}
                    onChange={(e) => setStillMarkAsBadTask(e.target.checked)}
                  />
                  <span className="checkbox-text">
                    Still mark problem as Bad Task
                    <span className="checkbox-hint">
                      (I disagree with the dispute reasoning, but the problem is still bad for a different reason)
                    </span>
                  </span>
                </label>
              </div>
            )}

            {/* Show final status section for non-bad-task disputes */}
            {!isBadTaskFromLabelPlatform && (
              <div className="final-status-section">
                <label>Select Final Run Status:</label>
                <div className="status-options">
                  <button
                    type="button"
                    className={`status-option ${selectedStatus === 'success' ? 'selected' : ''}`}
                    onClick={() => setSelectedStatus('success')}
                    disabled={!disputeDecision}
                  >
                    ✓ Success
                  </button>
                  <button
                    type="button"
                    className={`status-option ${selectedStatus === 'failed' ? 'selected' : ''}`}
                    onClick={() => setSelectedStatus('failed')}
                    disabled={!disputeDecision}
                  >
                    ✗ Failed
                  </button>
                  <button
                    type="button"
                    className={`status-option ${selectedStatus === 'bad_task' ? 'selected' : ''}`}
                    onClick={() => setSelectedStatus('bad_task')}
                    disabled={!disputeDecision}
                  >
                    ⚠ Bad Task
                  </button>
                </div>
              </div>
            )}
            
            <label htmlFor="dispute-reason">
              Please provide a reason for your decision:
            </label>
            <textarea
              id="dispute-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why you are accepting or rejecting this dispute..."
              rows={5}
              required
            />
            
            <div className="dispute-response-modal-actions">
              <button
                type="button"
                className="submit-btn"
                onClick={handleSubmit}
                disabled={
                  !disputeDecision ||
                  !reason.trim() ||
                  // Don't require selectedStatus for bad task disputes (auto-set in handleDisputeDecisionClick)
                  (!isBadTaskFromLabelPlatform && !selectedStatus) ||
                  isSubmitting
                }
              >
                {isSubmitting ? 'Processing...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Warning Modal */}
      {showWarningModal && disputeDecision && (
        <div className="warning-modal-overlay" onClick={() => setShowWarningModal(false)}>
          <div className="warning-modal" onClick={(e) => e.stopPropagation()}>
            <div className="warning-modal-header">
              <h3>⚠️ Status Override Warning</h3>
            </div>
            <div className="warning-modal-content">
              <p>
                Warning: You have <strong>{disputeDecision}</strong> the dispute, which would typically mean that the task status is <strong>{getExpectedStatus(disputeDecision)}</strong>.
              </p>
              <p>
                However, you are about to set the status of the task to <strong>{selectedStatus ? getStatusDisplayName(selectedStatus) : ''}</strong>.
              </p>
              <p>Are you sure you want to continue?</p>
            </div>
            <div className="warning-modal-actions">
              <button 
                type="button" 
                className="cancel-btn" 
                onClick={() => setShowWarningModal(false)}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="confirm-btn" 
                onClick={() => {
                  setShowWarningModal(false);
                  if (disputeDecision) {
                    submitDispute(disputeDecision);
                  }
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisputeResponseModal;